import { syncHttpRequest2 } from 'utils/http';
import { words } from './globalState';
import { getRegisteredCard } from './blockchain';
import {
  getAddressFromKey,
  signWithKey,
  getValidKeyPairs,
} from './keysAndSigning';
import {
  getNineSliceSizesFromImages,
  getHorizontalThreeSliceSizesFromImages,
  getScaledHeightForWidth,
  getContainedImageSize,
  normalizeHorizontalThreeSliceSize,
  normalizeNineSliceSize,
  loadImageForCanvas,
  canvasToBlob,
  downloadBlobAsPng,
  wrapCanvasText,
  trimTrailingBlankLines,
  drawImageContain,
  drawHorizontalThreeSliceImage,
  drawNineSliceImage,
  drawRoundedStroke,
} from './utils';
import { copyToClipboard } from './utils/utilsBrowser';
import { magicNarration } from './utils/utilsKom';
import { devLog } from './dev/development';
import battleStoryTop from './image/battle_story_top.png';
import battleStoryMiddle from './image/battle_story_middle.png';
import battleStoryBottom from './image/battle_story_bottom.png';
import fieldStatusHeading from './image/field_status.png';
import straightManHeading from './image/straightman.png';
import duelFieldBackground from './image/background.png';
import knightBoxLTopLeft from './image/knightBox_L_top_left.png';
import knightBoxLTopCenter from './image/knightBox_L_top_center.png';
import knightBoxLTopRight from './image/knightBox_L_top_right.png';
import knightBoxLMiddleLeft from './image/knightBox_L_middle_left.png';
import knightBoxLMiddleCenter from './image/knightBox_L_middle_center.png';
import knightBoxLMiddleRight from './image/knightBox_L_middle_right.png';
import knightBoxLBottomLeft from './image/knightBox_L_bottom_left.png';
import knightBoxLBottomCenter from './image/knightBox_L_bottom_center.png';
import knightBoxLBottomRight from './image/knightBox_L_bottom_right.png';
import knightBoxRTopLeft from './image/knightBox_R_top_left.png';
import knightBoxRTopCenter from './image/knightBox_R_top_center.png';
import knightBoxRTopRight from './image/knightBox_R_top_right.png';
import knightBoxRMiddleLeft from './image/knightBox_R_middle_left.png';
import knightBoxRMiddleCenter from './image/knightBox_R_middle_center.png';
import knightBoxRMiddleRight from './image/knightBox_R_middle_right.png';
import knightBoxRBottomLeft from './image/knightBox_R_bottom_left.png';
import knightBoxRBottomCenter from './image/knightBox_R_bottom_center.png';
import knightBoxRBottomRight from './image/knightBox_R_bottom_right.png';
import emblemRed from './emblemRed.png';
import emblemBlue from './emblemBlue.png';
import imageMagicRed from './magicRed.png';
import imageMagicBlue from './magicBlue.png';
import logoWin from './logoWin.png';
import logoLose from './logoLose.png';
import logoDraw from './logoDraw.png';
import deckRibbonRedLeft from './image/deckRibbon_red_left.png';
import deckRibbonRedCenter from './image/deckRibbon_red_center.png';
import deckRibbonRedRight from './image/deckRibbon_red_right.png';
import deckRibbonBlueLeft from './image/deckRibbon_blue_left.png';
import deckRibbonBlueCenter from './image/deckRibbon_blue_center.png';
import deckRibbonBlueRight from './image/deckRibbon_blue_right.png';

// LOAD DECK
async function loadDeck({ session, dispatch, certificationsToOverride = [], marginOfSessionTime }) {
  let request = {};
  let messages = {};
  const now = new Date();

  const certifications = Object.keys(session)
  .filter( address => session[address].expirationOfSession > now.getTime() + marginOfSessionTime )
  .map( address => {
    return { addressMain: address, sessionId: session[address].sessionId };
  })
  .filter(
    certification => !certificationsToOverride.map( certificationToOverride => certificationToOverride.addressMain ).includes(certification.addressMain)
  );
  certifications.push(...certificationsToOverride);

  request.body = JSON.stringify({
    body: {
      certifications,
    }
  });

  request.url = 'https://' + process.env.REACT_APP_KOM_API_DOMAIN + '/load_deck';
  request.method = 'POST';

  messages = {
  };

  const response = await syncHttpRequest2(request, 'deckSet', undefined, undefined, messages, dispatch);

  if ( response.status === 'rejected' ) {
    const deckSets = {};

    for (const addressMain of Object.keys(session)) {
      deckSets[addressMain] = {
        currentDeckNo: 1,
        decks: [
          null,
          {
            deckName: 'deck1',
            cards: [
            ],
          },
        ],
      };
    }

    dispatch({ type: 'setState', key: 'deckSet', value: deckSets });

    return {
      status: 'rejected',
      body: deckSets,
    };
  }
  else {
    for (const addressMain of Object.keys(response.body)) {
      if (response.body[addressMain].applicationMessage === 'no deck') {
        const deckSet = {
          currentDeckNo: 1,
          decks: [
            null,
            {
              deckName: 'deck1',
              cards: [
              ],
            },
          ],
        };

        dispatch({ type: 'setStateMultiLayers', keys: ['deckSet', addressMain], value: deckSet });
      }
    }
  }

  return {
    status: 'fulfilled',
    body: response.body,
  };
}

// HANDLE CLICK CHALLENGE DUEL
function handleClickChallengeDuel({ navigate, gameMode, difficultyMode }) {
  if (gameMode === 'human') {
    if (difficultyMode === 'advanced') {
      navigate(process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/dueladvanced' : '/dueladvanced');
    }
    else {
      navigate(process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/duel' : '/duel');
    }
  }
  else { // computer
    if (difficultyMode === 'advanced') {
      navigate(process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/duelcomadvanced' : '/duelcomadvanced');
    }
    else {
      navigate(process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/duelcom' : '/duelcom');
    }
  }
}

// HANDLE CLICK GO TOP
function handleClickGoTop(state, dispatch, navigate) {
  navigate(process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/top' : '/top');
}

// HANDLE CLICK GO BACK
function handleClickGoBack(state, dispatch, navigate, location) {
  if (location?.state?.previousLocation !== undefined) {
    navigate(-1);
  }
  else {
    navigate(process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/top' : '/top');
  }
}

// HANDLE CLICK PURCHASE ITEM
function handleClickPurchaseItem(state, dispatch, navigate, navigateState) {
  if (navigateState !== undefined && navigateState !== null) {
    navigate((process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/purchaseitems' : '/purchaseitems'), { state: navigateState });
  }
  else {
    navigate(process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/purchaseitems' : '/purchaseitems');
  }
}

// HANDLE CLICK DUEL HISTORY
function handleClickDuelHistory(state, dispatch, navigate, navigateState) {
  if (navigateState !== undefined && navigateState !== null) {
    navigate((process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/duelhistory' : '/duelhistory'), { state: navigateState });
  }
  else {
    navigate(process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/duelhistory' : '/duelhistory');
  }
}

// HANDLE CLICK CARD LIST
function handleClickCardList(state, dispatch, navigate, navigateState) {
  if (navigateState !== undefined && navigateState !== null) {
    navigate((process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/cardlist' : '/cardlist'), { state: navigateState });
  }
  else {
    navigate(process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/cardlist' : '/cardlist');
  }
}

const duelHistoryControlCodeFinished = '<-KOM-finished->';

function buildDuelHistoryStoryText({ thisRound, magicText }) {
  const magicTextTrimmed = trimTrailingBlankLines(magicText);
  const liveTextTrimmed = trimTrailingBlankLines(thisRound.liveText);
  const fieldTextTrimmed = trimTrailingBlankLines(thisRound.field);
  const straightManTextReplaced = trimTrailingBlankLines(
    thisRound.straightManText?.replace(duelHistoryControlCodeFinished, '')
  );
  const texts = [
    magicTextTrimmed,
    liveTextTrimmed,
    fieldTextTrimmed === '' ? '<<フィールドの状況>>' : '<<フィールドの状況>>\n' + fieldTextTrimmed,
  ];

  if (straightManTextReplaced !== '') {
    texts.push('<<つっこみ>>\n' + straightManTextReplaced);
  }

  return texts.filter(text => text !== undefined && text !== null && text !== '').join('\n\n');
}

// HANDLE CLICK BUILD DECK
function handleClickBuildDeck(state, dispatch, navigate) {
  navigate(process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/builddecks' : '/builddecks');
}

// HANDLE CLICK DUEL RANKING
function handleClickDuelRanking(state, dispatch, navigate) {
  navigate(process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/duelranking' : '/duelranking');
}

// HANDLE CLICK CHARGE POINT
function handleClickChargePoint(state, dispatch, navigate) {
  navigate(process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/chargepoints' : '/chargepoints');
}

// HANDLE CLICK CARD REGISTRATION
function handleClickCardRegistration(state, dispatch, navigate) {
  navigate(process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/cardregistration' : '/cardregistration');
}

// HANDLE CLICK PLAYER STATS
function handleClickPlayerStats(state, dispatch, navigate) {
  navigate(process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/playerstats' : '/playerstats');
}

// HANDLE CLICK SETTING
function handleClickSetting(state, dispatch, navigate) {
  navigate(process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/setting' : '/setting');
}

function buildDuelHistoryStoryBlocks({ thisRound, magicText }) {
  const magicTextTrimmed = trimTrailingBlankLines(magicText);
  const liveTextTrimmed = trimTrailingBlankLines(thisRound.liveText);
  const fieldTextTrimmed = trimTrailingBlankLines(thisRound.field);
  const straightManTextReplaced = trimTrailingBlankLines(
    thisRound.straightManText?.replace(duelHistoryControlCodeFinished, '')
  );
  const blocks = [
    {
      type: 'text',
      text: magicTextTrimmed,
    },
    {
      type: 'text',
      text: liveTextTrimmed,
    },
    {
      type: 'heading',
      heading: 'fieldStatus',
      text: fieldTextTrimmed,
    },
  ];

  if (straightManTextReplaced !== '') {
    blocks.push({
      type: 'heading',
      heading: 'straightMan',
      text: straightManTextReplaced,
    });
  }

  return blocks.filter(block => block.type === 'heading' || (block.text !== undefined && block.text !== null && block.text !== ''));
}

async function createDuelHistoryStoryBlob({
  registeredCard,
  thisRound,
  scale = 1,
}) {
  const magicText = magicNarration({
    registeredCard,
  }, thisRound.magics, thisRound.initiative, thisRound.monsters);
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (context === null) {
    throw new Error('canvas context is not available.');
  }

  const width = 1000;
  const topHeight = 176;
  const bottomHeight = 153;
  const middleOverlap = 2;
  const padding = {
    top: 87,
    right: 45,
    bottom: 79,
    left: 45,
  };
  const fontSize = 31;
  const lineHeight = 46;
  const blockGap = lineHeight;
  const headingTextGap = 12;
  const maxHeadingHeight = 150;
  const textMaxWidth = width - padding.left - padding.right;

  const [topImage, middleImage, bottomImage, fieldStatusHeadingImage, straightManHeadingImage] = await Promise.all([
    loadImageForCanvas({ src: battleStoryTop }),
    loadImageForCanvas({ src: battleStoryMiddle }),
    loadImageForCanvas({ src: battleStoryBottom }),
    loadImageForCanvas({ src: fieldStatusHeading }),
    loadImageForCanvas({ src: straightManHeading }),
  ]);
  const headingImages = {
    fieldStatus: fieldStatusHeadingImage,
    straightMan: straightManHeadingImage,
  };

  context.font = `${fontSize}px sans-serif`;
  const storyBlocks = buildDuelHistoryStoryBlocks({ thisRound, magicText });
  const blockLayouts = storyBlocks.map(block => {
    const lines = block.text === ''
      ? []
      : wrapCanvasText({
        context,
        text: block.text,
        maxWidth: textMaxWidth,
      });
    const headingImage = block.type === 'heading' ? headingImages[block.heading] : undefined;
    const headingImageSize = headingImage !== undefined
      ? getContainedImageSize({
        image: headingImage,
        maxWidth: textMaxWidth,
        maxHeight: maxHeadingHeight,
      })
      : undefined;
    const headingHeight = headingImageSize !== undefined
      ? headingImageSize.height + (lines.length > 0 ? headingTextGap : 0)
      : 0;
    const textHeight = lines.length * lineHeight;

    return {
      ...block,
      lines,
      headingImage,
      headingImageSize,
      height: headingHeight + textHeight,
    };
  });
  const contentHeight = blockLayouts.reduce((totalHeight, blockLayout, index) => {
    return totalHeight + blockLayout.height + (index === blockLayouts.length - 1 ? 0 : blockGap);
  }, 0);
  const height = Math.max(820, padding.top + contentHeight + padding.bottom);
  const middleHeight = height - topHeight - bottomHeight + middleOverlap + middleOverlap;

  canvas.width = width;
  canvas.height = height;

  context.drawImage(topImage, 0, 0, width, topHeight);
  context.drawImage(middleImage, 0, topHeight - middleOverlap, width, middleHeight);
  context.drawImage(bottomImage, 0, height - bottomHeight, width, bottomHeight);

  context.fillStyle = '#000000';
  context.font = `${fontSize}px sans-serif`;
  context.textBaseline = 'top';

  let currentY = padding.top;

  blockLayouts.forEach((blockLayout, blockIndex) => {
    if (blockLayout.headingImage !== undefined && blockLayout.headingImageSize !== undefined) {
      const headingX = padding.left + (textMaxWidth - blockLayout.headingImageSize.width) / 2;
      context.drawImage(
        blockLayout.headingImage,
        headingX,
        currentY,
        blockLayout.headingImageSize.width,
        blockLayout.headingImageSize.height
      );
      currentY += blockLayout.headingImageSize.height + (blockLayout.lines.length > 0 ? headingTextGap : 0);
    }

    blockLayout.lines.forEach((line, lineIndex) => {
      context.fillText(line, padding.left, currentY + lineIndex * lineHeight);
    });
    currentY += blockLayout.lines.length * lineHeight;

    if (blockIndex !== blockLayouts.length - 1) {
      currentY += blockGap;
    }
  });

  return canvasToBlob({ canvas, scale });
}

async function copyDuelHistoryStoryToClipboard({
  dispatch,
  language,
  registeredCard,
  thisRound,
  textAreaRef,
}) {
  const magicText = magicNarration({
    registeredCard,
  }, thisRound.magics, thisRound.initiative, thisRound.monsters);
  const storyText = buildDuelHistoryStoryText({ thisRound, magicText });

  try {
    if (navigator.clipboard?.write === undefined || window.ClipboardItem === undefined) {
      throw new Error('clipboard image copy is not supported.');
    }
    const blob = await createDuelHistoryStoryBlob({ registeredCard, thisRound });
    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': blob }),
    ]);
    dispatch({ type: 'setNotification', key: 'notification', value: words.imageCopiedToClipboard[language] });
  }
  catch {
    (navigator.clipboard?.writeText !== undefined ? navigator.clipboard.writeText(storyText) : Promise.reject())
    .then(() => {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToCopyImageCopiedTextToClipboard[language] });
    })
    .catch(() => {
      copyToClipboard(storyText, textAreaRef);
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToCopyImageCopiedTextToClipboard[language] });
    });
  }
}

async function downloadDuelHistoryStoryImage({
  dispatch,
  language,
  registeredCard,
  thisRound,
}) {
  try {
    const blob = await createDuelHistoryStoryBlob({ registeredCard, thisRound });
    downloadBlobAsPng({ blob, fileNameBase: 'battle_story' });
    dispatch({ type: 'setNotification', key: 'notification', value: words.imageDownloaded[language] });
  }
  catch {
    dispatch({ type: 'setNotification', key: 'notification', value: words.failedToDownloadImage[language] });
  }
}

function getBattleResultLabel({ side, winner }) {
  if (winner === side) {
    return 'WIN';
  }
  else if (winner === 'draw') {
    return 'DRAW';
  }
  else if (winner !== undefined && winner !== null) {
    return 'LOSE';
  }
  else {
    return '';
  }
}

function getMonacardImageUrl({ asset, imageNo, small = true }) {
  if (asset === undefined) {
    return undefined;
  }

  const imageName = imageNo !== undefined && imageNo !== 0
    ? `${asset}_${imageNo}`
    : asset;

  return small
    ? `https://${process.env.REACT_APP_MONACARD_URL}small/${imageName}_small.png`
    : `https://${process.env.REACT_APP_MONACARD_URL}${imageName}.png`;
}

const KNIGHT_BOX_IMAGE_COPY = {
  canvasWidth: 1000,
  boxWidth: 450,
  resultLogoDrawWidth: 380,
  boxX: { A: 35, B: 515 },
  ribbonY: 15,
  ribbonOverlap: 25,
  resultLogoOverlap: 40,
  canvasBottomMargin: 16,
  panelPadding: { top: 50, right: 50, bottom: 55, left: 50 },
  cardAreaWidth: 380,
  cardAreaHeightAdvanced: 543,
  cardAreaHeightSimple: 543,
  panelBorderRadius: 8,
  panelBorderLineWidth: 6,
  panelSliceOverlap: 1,
  sideBorderColor: {
    A: 'rgba(255, 58, 58, 0.4)',
    B: 'rgba(58, 58, 255, 0.4)',
  },
  typography: {
    userName: '20px sans-serif',
    monsterName: '20px sans-serif',
    magicAppliedMp: '22px sans-serif',
    magicName: '18px sans-serif',
  },
  spacing: {
    monsterNameGap: 12,
    monsterNameLineHeight: 24,
    magicSectionGap: 8,
    magicTextTopOffset: 20,
    magicAppliedMpLineHeight: 24,
    magicNameGap: 10,
    magicNameLineHeight: 24,
    magicImageMarginLeft: 40,
    magicTextMarginLeft: 140,
    magicTextMarginRight: 20,
  },
  magicImageDraw: {
    width: 80,
    height: 110,
  },
  sliceDefaults: {
    ribbon: {
      height: 48,
      leftSideWidth: 114,
      rightSideWidth: 114,
    },
    panel: {
      sideWidth: 32,
      topHeight: 42,
      bottomHeight: 42,
      minCenterWidthRatio: 0.2,
      minCenterHeightRatio: 0.2,
    },
  },
};

function getKnightBoxSideLayout({
  difficultyMode,
  registeredCard,
  thisRound,
  side,
  cardArea,
}) {
  const { panelPadding, spacing, magicImageDraw, boxWidth } = KNIGHT_BOX_IMAGE_COPY;
  const monsterAsset = thisRound.monsters?.[side];
  const monsterName = thisRound.monstersInformationRevised?.[side]?.name
    ?? registeredCard?.[monsterAsset]?.name
    ?? '';
  const magic = thisRound.magics?.[side]?.[0];
  const magicAsset = magic?.asset;
  const magicName = registeredCard?.[magicAsset]?.name ?? '';

  let contentHeight = panelPadding.top + cardArea.height;

  if (monsterName !== '') {
    contentHeight += spacing.monsterNameGap + spacing.monsterNameLineHeight;
  }

  let magicSectionOffset;
  let magicSection;

  if (difficultyMode === 'advanced') {
    magicSectionOffset = contentHeight + spacing.magicSectionGap;

    const hasAppliedMp = magic !== undefined && thisRound.maginaPoint?.applied !== undefined;
    const hasMagicName = magicName !== '';
    let magicTextColumnHeight = 0;
    let appliedMpOffsetY;
    let magicNameOffsetY;
    let textY = spacing.magicTextTopOffset;

    if (hasAppliedMp) {
      appliedMpOffsetY = textY;
      textY += spacing.magicAppliedMpLineHeight;
    }

    if (hasMagicName) {
      if (hasAppliedMp) {
        textY += spacing.magicNameGap;
      }
      magicNameOffsetY = textY;
      textY += spacing.magicNameLineHeight;
    }

    if (hasAppliedMp || hasMagicName) {
      magicTextColumnHeight = textY;
    }

    const magicContentHeight = Math.max(
      magicImageDraw.height,
      magicTextColumnHeight
    );
    contentHeight = magicSectionOffset + magicContentHeight;
    magicSection = {
      imageX: spacing.magicImageMarginLeft,
      imageWidth: magicImageDraw.width,
      imageHeight: magicImageDraw.height,
      textX: spacing.magicTextMarginLeft,
      appliedMpOffsetY,
      magicNameOffsetY,
      textMaxWidth: boxWidth - spacing.magicTextMarginLeft - spacing.magicTextMarginRight,
    };
  }

  return {
    monsterName,
    magic,
    magicName,
    boxHeight: contentHeight + panelPadding.bottom,
    magicSectionOffset,
    monsterNameY: panelPadding.top + cardArea.height + spacing.monsterNameGap,
    magicSection,
  };
}

function buildKnightBoxImageCopyLayout({
  record,
  registeredCard,
  thisRound,
  loadedPanelImages,
  loadedRibbonImages,
  resultLogoImages,
}) {
  const rawRibbonSlice = getHorizontalThreeSliceSizesFromImages({
    leftImage: loadedRibbonImages.A.left,
    rightImage: loadedRibbonImages.A.right,
  });
  const rawPanelSlice = getNineSliceSizesFromImages({
    topLeftImage: loadedPanelImages.A.topLeft,
    bottomLeftImage: loadedPanelImages.A.bottomLeft,
  });
  const cardArea = {
    width: KNIGHT_BOX_IMAGE_COPY.cardAreaWidth,
    height: record.difficultyMode === 'advanced'
      ? KNIGHT_BOX_IMAGE_COPY.cardAreaHeightAdvanced
      : KNIGHT_BOX_IMAGE_COPY.cardAreaHeightSimple,
  };
  const sideLayouts = Object.fromEntries(
    ['A', 'B'].map(side => [
      side,
      getKnightBoxSideLayout({
        difficultyMode: record.difficultyMode,
        registeredCard,
        thisRound,
        side,
        cardArea,
      }),
    ])
  );
  const boxHeight = Math.max(sideLayouts.A.boxHeight, sideLayouts.B.boxHeight);
  const ribbonSlice = normalizeHorizontalThreeSliceSize({
    rawSlice: rawRibbonSlice,
    targetWidth: KNIGHT_BOX_IMAGE_COPY.boxWidth,
    targetHeight: KNIGHT_BOX_IMAGE_COPY.sliceDefaults.ribbon.height,
    fallbackSlice: KNIGHT_BOX_IMAGE_COPY.sliceDefaults.ribbon,
  });
  const panelSlice = normalizeNineSliceSize({
    rawSlice: rawPanelSlice,
    targetWidth: KNIGHT_BOX_IMAGE_COPY.boxWidth,
    targetHeight: boxHeight,
    fallbackSlice: KNIGHT_BOX_IMAGE_COPY.sliceDefaults.panel,
    overlap: KNIGHT_BOX_IMAGE_COPY.panelSliceOverlap,
    minCenterWidthRatio: KNIGHT_BOX_IMAGE_COPY.sliceDefaults.panel.minCenterWidthRatio,
    minCenterHeightRatio: KNIGHT_BOX_IMAGE_COPY.sliceDefaults.panel.minCenterHeightRatio,
  });
  const boxTop = KNIGHT_BOX_IMAGE_COPY.ribbonY + ribbonSlice.height - KNIGHT_BOX_IMAGE_COPY.ribbonOverlap;
  const resultLogoDrawWidth = KNIGHT_BOX_IMAGE_COPY.resultLogoDrawWidth;
  const resultLogoDrawHeight = getScaledHeightForWidth({
    image: resultLogoImages.WIN,
    targetWidth: resultLogoDrawWidth,
  });
  const resultLogoX = Object.fromEntries(
    ['A', 'B'].map(side => [
      side,
      KNIGHT_BOX_IMAGE_COPY.boxX[side] + (KNIGHT_BOX_IMAGE_COPY.boxWidth - resultLogoDrawWidth) / 2,
    ])
  );

  return {
    canvasWidth: KNIGHT_BOX_IMAGE_COPY.canvasWidth,
    canvasHeight: boxTop + boxHeight + (resultLogoDrawHeight - KNIGHT_BOX_IMAGE_COPY.resultLogoOverlap) + KNIGHT_BOX_IMAGE_COPY.canvasBottomMargin,
    ribbonSlice,
    panelSlice,
    boxTop,
    boxHeight,
    cardArea,
    sideLayouts,
    resultLogoX,
    resultLogoDrawWidth,
    resultLogoDrawHeight,
    resultLogoY: boxTop + boxHeight - KNIGHT_BOX_IMAGE_COPY.resultLogoOverlap,
  };
}

async function createDuelHistoryKnightBoxesBlob({
  record,
  registeredCard,
  round,
  scale = 1,
}) {
  const thisRound = record.result[round];
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (context === null) {
    throw new Error('canvas context is not available.');
  }

  const panelImages = {
    A: {
      topLeft: knightBoxLTopLeft,
      topCenter: knightBoxLTopCenter,
      topRight: knightBoxLTopRight,
      middleLeft: knightBoxLMiddleLeft,
      middleCenter: knightBoxLMiddleCenter,
      middleRight: knightBoxLMiddleRight,
      bottomLeft: knightBoxLBottomLeft,
      bottomCenter: knightBoxLBottomCenter,
      bottomRight: knightBoxLBottomRight,
    },
    B: {
      topLeft: knightBoxRTopLeft,
      topCenter: knightBoxRTopCenter,
      topRight: knightBoxRTopRight,
      middleLeft: knightBoxRMiddleLeft,
      middleCenter: knightBoxRMiddleCenter,
      middleRight: knightBoxRMiddleRight,
      bottomLeft: knightBoxRBottomLeft,
      bottomCenter: knightBoxRBottomCenter,
      bottomRight: knightBoxRBottomRight,
    },
  };
  const ribbonImages = {
    A: {
      left: deckRibbonRedLeft,
      center: deckRibbonRedCenter,
      right: deckRibbonRedRight,
    },
    B: {
      left: deckRibbonBlueLeft,
      center: deckRibbonBlueCenter,
      right: deckRibbonBlueRight,
    },
  };
  const sideImageSources = Object.fromEntries(
    ['A', 'B'].map(side => {
      const monsterAsset = thisRound.monsters?.[side];
      const monsterImageNo = thisRound.imageNo?.[side];
      const magicAsset = thisRound.magics?.[side]?.[0]?.asset;

      return [
        side,
        {
          cardImageUrl: getMonacardImageUrl({ asset: monsterAsset, imageNo: monsterImageNo }),
          magicImageUrl: getMonacardImageUrl({ asset: magicAsset }),
        },
      ];
    })
  );
  const [loadedPanelImagesEntries, staticImages, sideImageEntries, loadedRibbonImagesEntries] = await Promise.all([
    Promise.all(
      ['A', 'B'].map(async side => {
        const panelImageEntries = await Promise.all(
          Object.entries(panelImages[side]).map(async ([key, imagePath]) => {
            const image = await loadImageForCanvas({ src: imagePath });
            return [key, image];
          })
        );

        return [side, Object.fromEntries(panelImageEntries)];
      })
    ),
    Promise.all([
      loadImageForCanvas({ src: duelFieldBackground }),
      loadImageForCanvas({ src: emblemRed }),
      loadImageForCanvas({ src: emblemBlue }),
      loadImageForCanvas({ src: imageMagicRed }),
      loadImageForCanvas({ src: imageMagicBlue }),
      loadImageForCanvas({ src: logoWin }),
      loadImageForCanvas({ src: logoLose }),
      loadImageForCanvas({ src: logoDraw }),
    ]),
    Promise.all(
      ['A', 'B'].map(async side => {
        const { cardImageUrl, magicImageUrl } = sideImageSources[side];
        const [cardImage, magicImage] = await Promise.all([
          cardImageUrl === undefined
            ? Promise.resolve(undefined)
            : loadImageForCanvas({ src: cardImageUrl, crossOrigin: 'anonymous' }).catch(() => undefined),
          magicImageUrl === undefined
            ? Promise.resolve(undefined)
            : loadImageForCanvas({ src: magicImageUrl, crossOrigin: 'anonymous' }).catch(() => undefined),
        ]);

        return [side, { cardImage, magicImage }];
      })
    ),
    Promise.all(
      ['A', 'B'].map(async side => {
        const ribbonImageEntries = await Promise.all(
          Object.entries(ribbonImages[side]).map(async ([key, imagePath]) => {
            const image = await loadImageForCanvas({ src: imagePath });
            return [key, image];
          })
        );

        return [side, Object.fromEntries(ribbonImageEntries)];
      })
    ),
  ]);
  const loadedPanelImages = Object.fromEntries(loadedPanelImagesEntries);
  const loadedRibbonImages = Object.fromEntries(loadedRibbonImagesEntries);
  const [
    backgroundImage,
    emblemRedImage,
    emblemBlueImage,
    magicRedImage,
    magicBlueImage,
    logoWinImage,
    logoLoseImage,
    logoDrawImage,
  ] = staticImages;
  const emblemImages = {
    A: emblemRedImage,
    B: emblemBlueImage,
  };
  const magicFallbackImages = {
    A: magicRedImage,
    B: magicBlueImage,
  };
  const loadedSideImages = Object.fromEntries(
    sideImageEntries.map(([side, images]) => {
      return [
        side,
        {
          cardImage: images.cardImage ?? emblemImages[side],
          magicImage: images.magicImage ?? magicFallbackImages[side],
        },
      ];
    })
  );
  const resultLogoImages = {
    WIN: logoWinImage,
    LOSE: logoLoseImage,
    DRAW: logoDrawImage,
  };
  const layout = buildKnightBoxImageCopyLayout({
    record,
    registeredCard,
    thisRound,
    loadedPanelImages,
    loadedRibbonImages,
    resultLogoImages,
  });

  canvas.width = layout.canvasWidth;
  canvas.height = layout.canvasHeight;
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, layout.canvasWidth, layout.canvasHeight);

  for (let x = 0; x < layout.canvasWidth; x += backgroundImage.width) {
    for (let y = 0; y < layout.canvasHeight; y += backgroundImage.height) {
      context.drawImage(backgroundImage, x, y);
    }
  }

  for (const side of ['A', 'B']) {
    const x = KNIGHT_BOX_IMAGE_COPY.boxX[side];
    const userName = side === 'A' ? record.duelistAUserName : record.duelistBUserName;
    const {
      monsterName,
      magic,
      magicName,
      magicSectionOffset,
      monsterNameY,
      magicSection,
    } = layout.sideLayouts[side];
    const cardImage = loadedSideImages[side].cardImage;
    const ribbonTextMaxWidth = KNIGHT_BOX_IMAGE_COPY.boxWidth
      - layout.ribbonSlice.leftSideWidth
      - layout.ribbonSlice.rightSideWidth;

    drawNineSliceImage({
      context,
      images: loadedPanelImages[side],
      x,
      y: layout.boxTop,
      width: KNIGHT_BOX_IMAGE_COPY.boxWidth,
      height: layout.boxHeight,
      sideWidth: layout.panelSlice.sideWidth,
      topHeight: layout.panelSlice.topHeight,
      bottomHeight: layout.panelSlice.bottomHeight,
      overlap: KNIGHT_BOX_IMAGE_COPY.panelSliceOverlap,
    });
    drawRoundedStroke({
      context,
      x,
      y: layout.boxTop,
      width: KNIGHT_BOX_IMAGE_COPY.boxWidth,
      height: layout.boxHeight,
      radius: KNIGHT_BOX_IMAGE_COPY.panelBorderRadius,
      strokeStyle: KNIGHT_BOX_IMAGE_COPY.sideBorderColor[side],
      lineWidth: KNIGHT_BOX_IMAGE_COPY.panelBorderLineWidth,
    });
    drawHorizontalThreeSliceImage({
      context,
      images: loadedRibbonImages[side],
      x,
      y: KNIGHT_BOX_IMAGE_COPY.ribbonY,
      width: KNIGHT_BOX_IMAGE_COPY.boxWidth,
      height: layout.ribbonSlice.height,
      leftSideWidth: layout.ribbonSlice.leftSideWidth,
      rightSideWidth: layout.ribbonSlice.rightSideWidth,
    });

    context.fillStyle = '#000000';
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.font = KNIGHT_BOX_IMAGE_COPY.typography.userName;
    context.fillText(
      userName ?? '',
      x + KNIGHT_BOX_IMAGE_COPY.boxWidth / 2,
      KNIGHT_BOX_IMAGE_COPY.ribbonY + layout.ribbonSlice.height / 2,
      ribbonTextMaxWidth
    );

    drawImageContain({
      context,
      image: cardImage,
      x: x + (KNIGHT_BOX_IMAGE_COPY.boxWidth - layout.cardArea.width) / 2,
      y: layout.boxTop + KNIGHT_BOX_IMAGE_COPY.panelPadding.top,
      width: layout.cardArea.width,
      height: layout.cardArea.height,
    });

    if (monsterName !== '') {
      context.fillStyle = '#000000';
      context.textAlign = 'center';
      context.textBaseline = 'top';
      context.font = KNIGHT_BOX_IMAGE_COPY.typography.monsterName;
      context.fillText(
        monsterName,
        x + KNIGHT_BOX_IMAGE_COPY.boxWidth / 2,
        layout.boxTop + monsterNameY,
        layout.cardArea.width
      );
    }

    if (magicSection !== undefined) {
      const magicImage = loadedSideImages[side].magicImage;
      const magicSectionY = layout.boxTop + magicSectionOffset;

      drawImageContain({
        context,
        image: magicImage,
        x: x + magicSection.imageX,
        y: magicSectionY,
        width: magicSection.imageWidth,
        height: magicSection.imageHeight,
      });

      context.fillStyle = '#000000';
      context.textAlign = 'left';
      context.textBaseline = 'top';
      context.font = KNIGHT_BOX_IMAGE_COPY.typography.magicAppliedMp;

      if (magic !== undefined && thisRound.maginaPoint?.applied !== undefined && magicSection.appliedMpOffsetY !== undefined) {
        context.fillText(
          String(thisRound.maginaPoint.applied.point),
          x + magicSection.textX,
          magicSectionY + magicSection.appliedMpOffsetY
        );
      }

      if (magicName !== '' && magicSection.magicNameOffsetY !== undefined) {
        context.font = KNIGHT_BOX_IMAGE_COPY.typography.magicName;
        context.fillText(
          magicName,
          x + magicSection.textX,
          magicSectionY + magicSection.magicNameOffsetY,
          magicSection.textMaxWidth
        );
      }
    }

    const result = getBattleResultLabel({ side, winner: thisRound.winner });

    if (result !== '') {
      drawImageContain({
        context,
        image: resultLogoImages[result],
        x: layout.resultLogoX[side],
        y: layout.resultLogoY,
        width: layout.resultLogoDrawWidth,
        height: layout.resultLogoDrawHeight,
      });
    }
  }

  return canvasToBlob({ canvas, scale });
}

async function copyDuelHistoryKnightBoxesToClipboard({
  dispatch,
  language,
  record,
  registeredCard,
  round,
  textAreaRef,
}) {
  const thisRound = record.result[round];
  const fallbackText = ['A', 'B'].map(side => {
    const userName = side === 'A' ? record.duelistAUserName : record.duelistBUserName;
    const asset = thisRound.monsters?.[side];
    const cardName = thisRound.monstersInformationRevised?.[side]?.name ?? registeredCard?.[asset]?.name ?? asset ?? '';
    const result = getBattleResultLabel({ side, winner: thisRound.winner });

    return `${side}: ${userName} ${cardName} ${result}`.trim();
  }).join('\n');

  try {
    if (navigator.clipboard?.write === undefined || window.ClipboardItem === undefined) {
      throw new Error('clipboard image copy is not supported.');
    }
    const blob = await createDuelHistoryKnightBoxesBlob({ record, registeredCard, round });
    await navigator.clipboard.write([
      new ClipboardItem({ 'image/png': blob }),
    ]);
    dispatch({ type: 'setNotification', key: 'notification', value: words.imageCopiedToClipboard[language] });
  }
  catch {
    (navigator.clipboard?.writeText !== undefined ? navigator.clipboard.writeText(fallbackText) : Promise.reject())
    .then(() => {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToCopyImageCopiedTextToClipboard[language] });
    })
    .catch(() => {
      copyToClipboard(fallbackText, textAreaRef);
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToCopyImageCopiedTextToClipboard[language] });
    });
  }
}

async function downloadDuelHistoryKnightBoxesImage({
  dispatch,
  language,
  record,
  registeredCard,
  round,
}) {
  try {
    const blob = await createDuelHistoryKnightBoxesBlob({ record, registeredCard, round });
    downloadBlobAsPng({ blob, fileNameBase: 'knights_and_spells' });
    dispatch({ type: 'setNotification', key: 'notification', value: words.imageDownloaded[language] });
  }
  catch {
    dispatch({ type: 'setNotification', key: 'notification', value: words.failedToDownloadImage[language] });
  }
}

// HANDLE CLICK CHECK OUT
function handleClickCheckOut(state, dispatch, navigate, navigateState) {
  if (navigateState !== undefined && navigateState !== null) {
    navigate((process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/checkout' : '/checkout'), { state: navigateState });
  }
  else {
    navigate(process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/checkout' : '/checkout');
  }
}

// HANDLE CLICK ITEM ORDER HISTORY
function handleClickItemOrderHistory(state, dispatch, navigate, navigateState) {
  if (navigateState !== undefined && navigateState !== null) {
    navigate((process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/itemorderhistory' : '/itemorderhistory'), { state: navigateState });
  }
  else {
    navigate(process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/itemorderhistory' : '/itemorderhistory');
  }
}

// HANDLE CLICK ACTIVATION SCREEN
function handleClickActivationScreen(navigate) {
  navigate(process.env.REACT_APP_ENVIRONMENT === 'dev' ? '/build/retailer' : '/retailer');
}

// HANDLE CLICK CLEAR MNEMONIC
function handleClickClearMnemonic(state, dispatch) {
  dispatch({ type: 'setStateMultiLayers', keys: ['displaySecret', 'mnemonic'], value: '' });

  return;
}

// HANDLE CLICK DISLPAY MNEMONIC
function handleClickDisplayMnemonic(state, dispatch, mnemonics) {
  dispatch({ type: 'setStateMultiLayers', keys: ['displaySecret', 'mnemonic'], value: mnemonics[0] });
  dispatch({ type: 'setStateMultiLayers', keys: ['displaySecret', 'mnemonicHidden'], value: mnemonics[0] });

  return;
}

// HANDLE CLICK CLEAR WIF
// eslint-disable-next-line no-unused-vars
function handleClickClearWif(state, dispatch) {
  dispatch({ type: 'setStateMultiLayers', keys: ['displaySecret', 'wif'], value: '' });

  return;
}

// HANDLE CLICK DISLPAY WIF
function handleClickDisplayWif(state, dispatch, mnemonics, keyPairs) {
  dispatch({ type: 'setStateMultiLayers', keys: ['displaySecret', 'mnemonicHidden'], value: mnemonics[0] });
  dispatch({ type: 'setStateMultiLayers', keys: ['displaySecret', 'wif'], value: keyPairs[0].toWIF() });

  return;
}

// ADD CARD TO DECK
function addCardToDeck(state, dispatch, cardTarget) {
  const addressMain = state.active.addressMain;
  const currentDeckNo = state.deckSet[addressMain].currentDeckNo;
  const currentDeckCards = state.deckSet[addressMain].decks[currentDeckNo].cards;

  if (currentDeckCards.length >= state.config.clientParameters.numberOfSlots.advanced) {
    return {
      status: 'rejected',
    };
  }
  else if (currentDeckCards.filter( card => card.asset === cardTarget.asset ).length < cardTarget.quantity) {
    const cardNew = {
      asset: cardTarget.asset,
      // assetCommon: assetCommon(state, cardTarget.asset),
      // imgur_url: state.monacard[cardTarget.asset].imgur_url,
    }
    currentDeckCards.push(cardNew);
    dispatch({ type: 'setStateMultiLayers', keys: ['deckSet', addressMain, 'decks', currentDeckNo, 'cards'], value: currentDeckCards });

    return {
      status: 'fulfilled',
      body: currentDeckCards,
    };
  }
  else {
    return {
      status: 'rejected',
    };
  }
}

// REMOVE CARD FROM DECK
function removeCardFromDeck(state, dispatch, asset) {
  const addressMain = state.active.addressMain;
  const currentDeckNo = state.deckSet[addressMain].currentDeckNo;
  const currentDeckCards = state.deckSet[addressMain].decks[currentDeckNo].cards;

  for (let i = currentDeckCards.length - 1; i >= 0; i--) {
    if (currentDeckCards[i].asset === asset) {
      currentDeckCards.splice(i, 1);
      break;
    }
  }

  dispatch({ type: 'setStateMultiLayers', keys: ['deckSet', addressMain, 'decks', currentDeckNo, 'cards'], value: currentDeckCards });

  return {
    status: 'fulfilled',
    body: currentDeckCards,
  };
}

// クリエイター情報を state.registeredCard の値にリセット
function resetCreatorInformationToRegisteredCard({ dispatch, asset, registeredCard }) {
  if (registeredCard === undefined) {
    return;
  }

  const monacottoAddressMain = registeredCard.monacottoLink?.url?.match(/mainaddress=(\w+)$/)?.[1];

  dispatch({ type: 'setStateMultiLayers', keys: ['creatorInformation', asset, 'creater'], value: registeredCard.creater ?? '' });
  dispatch({ type: 'setStateMultiLayers', keys: ['creatorInformation', asset, 'createrText'], value: registeredCard.createrText ?? '' });
  dispatch({ type: 'setStateMultiLayers', keys: ['creatorInformation', asset, 'monacottoAddressMain'], value: monacottoAddressMain ?? '' });
  dispatch({ type: 'setStateMultiLayers', keys: ['creatorInformation', asset, 'createrLink', 0, 'title'], value: registeredCard?.createrLink?.[0]?.title ?? '' });
  dispatch({ type: 'setStateMultiLayers', keys: ['creatorInformation', asset, 'createrLink', 0, 'url'], value: registeredCard?.createrLink?.[0]?.url ?? '' });
  dispatch({ type: 'setStateMultiLayers', keys: ['creatorInformation', asset, 'createrLink', 1, 'title'], value: registeredCard?.createrLink?.[1]?.title ?? '' });
  dispatch({ type: 'setStateMultiLayers', keys: ['creatorInformation', asset, 'createrLink', 1, 'url'], value: registeredCard?.createrLink?.[1]?.url ?? '' });
  dispatch({ type: 'setStateMultiLayers', keys: ['creatorInformation', asset, 'createrLink', 2, 'title'], value: registeredCard?.createrLink?.[2]?.title ?? '' });
  dispatch({ type: 'setStateMultiLayers', keys: ['creatorInformation', asset, 'createrLink', 2, 'url'], value: registeredCard?.createrLink?.[2]?.url ?? '' });
}

// 「クリエイター情報編集」ボタン押下 HANDLE CLICK EDIT CREATOR INFORMATION
async function handleClickEditCreatorInformation(state, dispatch, asset, { keyPairs, keyPairsInPoolAll } = {}) {
  let addressMainActual;
  let signature;
  let request = {};
  let messages = {};
  let result;
  let keyPair;

  const creatorInformation = state.creatorInformation[asset] ?? {};

  // state固定
  const stateFixed = {
    // addressMain: state.cardRegistration.addressMain,
    creater: creatorInformation.creater,
    createrText: creatorInformation.createrText,
    monacottoAddressMain: creatorInformation.monacottoAddressMain,
    createrLink: creatorInformation.createrLink,
    // imageMain: state.cardRegistration.imagesNew.main,
    signatureVersion: state.config.clientParameters.signatureVersion.editCreatorInformation,
    // addressCheck: state.addressCheck,
  };

  // 必須項目チェック
  if (asset === undefined || asset === null || asset === '') {
    dispatch({ type: 'setNotification', key: 'notification', value: words.assetMustBeFilled[state.language] });
    return;
  }

  // // 文字数チェック
  // if (stateFixed.name.length > 25) {
  //   dispatch({ type: 'setNotification', key: 'notification', value: words.nameIsTooLong[state.language] });
  //   return;
  // }

  // if (stateFixed.feature.length > 70) {
  //   dispatch({ type: 'setNotification', key: 'notification', value: words.featureIsTooLong[state.language] });
  //   return;
  // }

  // アドレスは入力させていないので、ノーチェックで署名する。
  const clientTime = Date.now();

  let keyPairFromPool;
  let ownerAddress;
  if (keyPairsInPoolAll !== undefined && keyPairsInPoolAll !== null) {
    const keyPairsInPool = getValidKeyPairs({ dispatch, keyPairs: keyPairsInPoolAll });
    ownerAddress = state.assetInfo[asset]?.owner;
    keyPairFromPool = keyPairsInPool[ownerAddress]?.keyPair;
  }

  if (keyPairs !== undefined && keyPairs !== null) {
    let result;

    keyPair = keyPairs[0];
    result = getAddressFromKey(state, dispatch, keyPair, state.networkMona);

    if (result.status === 'fulfilled') {
      addressMainActual = result.address;
    }
    else {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToGetTheAddress[state.language] });
      return result;
    }

    // 署名
    const message = `I edit the creator information of ${asset}. My main address is ${addressMainActual}. I am the owner of the card. (epoch time: ${clientTime}, signature version: ${stateFixed.signatureVersion})`;
    devLog('message', message);

    result = signWithKey(state, dispatch, keyPair, message, state.networkMona);

    if (result.status === 'fulfilled') {
      signature = result.signature;
    }
    else {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToSign[state.language] });
      return 'rejected';
    }
  }
  else if (keyPairFromPool !== undefined && keyPairFromPool !== null) {
    keyPair = keyPairFromPool;
    addressMainActual = ownerAddress;

    // 署名
    const message = `I edit the creator information of ${asset}. My main address is ${addressMainActual}. I am the owner of the card. (epoch time: ${clientTime}, signature version: ${stateFixed.signatureVersion})`;
    devLog('message', message);

    result = signWithKey(state, dispatch, keyPair, message, state.networkMona);

    if (result.status === 'fulfilled') {
      signature = result.signature;
    }
    else {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToSign[state.language] });
      return 'rejected';
    }
  }
  else {
    result = await window.mpurse.getAddress()
    .then( (result) => {
      addressMainActual = result;

      // if (stateFixed.addressCheck === 'strict' || stateFixed.addressCheck === 'addressSecond') {
      //   if (addressMainActual !== stateFixed.addressMain) {
      //     dispatch({ type: 'setNotification', key: 'notification', value: words.theAddressFilledAndTheAddressInWalletAreDifferent[state.language] });
      //     return 'rejected';
      //   }
      //   else {
      //     return 'fulfilled';
      //   }
      // }
      // else { // off
      //   return 'fulfilled';
      // }

      return 'fulfilled';
    })
    .catch( (error) => {
        dispatch({ type: 'setNotification', key: 'notification', value: words.theAddressInYourWalletCanNotBeGotten[state.language] });
        return 'rejected';
    });

    if (result === 'rejected') {
      return { status: 'rejected' };
    }

    // 署名
    const message = `I edit the creator information of ${asset}. My main address is ${addressMainActual}. I am the owner of the card. (epoch time: ${clientTime}, signature version: ${stateFixed.signatureVersion})`;
    devLog('message', message);

    result = await window.mpurse.signMessage(message)
    .then( result => {
      signature = result;
      return 'fulfilled';
    })
    .catch( err => {
      dispatch({ type: 'setNotification', key: 'notification', value: words.failedToSign[state.language] });
      return 'rejected';
    });

    if (result === 'rejected') {
      return { status: 'rejected' };
    }
  }

  const formData = new FormData();

  const requestJson = JSON.stringify({
    body: {
      addressMain: addressMainActual,
      addressMainActual: addressMainActual,
      asset: asset,
      creater: stateFixed.creater,
      createrText: stateFixed.createrText,
      monacottoAddressMain: stateFixed.monacottoAddressMain,
      createrLink: stateFixed.createrLink,
      clientTime : clientTime,
      signature : signature,
      signatureVersion : stateFixed.signatureVersion,
    }
  });

  formData.append('json', requestJson);

  // if (stateFixed.imageMain !== undefined && stateFixed.imageMain !== null) {
  //   formData.append('imageMain', stateFixed.imageMain, stateFixed.imageMain.name);
  // }

  request.body = formData;
  request.headers = {};
  request.url = 'https://' + process.env.REACT_APP_KOM_API_DOMAIN + '/edit_creator_information';
  request.method = 'POST';

  messages = {
    "you must accept the terms and conditions of the current version.": words.youMustAcceptTheTermsAndConditionsOfTheCurrentVersion[state.language],
    "internal server error": words.internalServerError[state.language],
    "bad signature": words.badSignature[state.language],
    "bad signature, can't recover address": words.badSignatureCanNotRecoverAddress[state.language],
    "unknown signatureVersion": words.unknownSignatureVersion[state.language],
    "clientTime must be larger than last time.": words.clientTimeMustBeLargerThanLastTime[state.language],
    "this address is not permitted.": words.thisAddressIsNotPermitted[state.language],
    "this address is forbidden.": words.thisAddressIsForbidden[state.language],
    "out of service": words.outOfService[state.language],
    "signature by asset owner is required.": words.signatureByAssetOwnerIsRequired[state.language],
    "asset is required; not asset_longname.": words.assetIsRequiredNotAssetLongname[state.language],
    // "you are not delegated.": words.norIsTheSignatureDelegatedFromMainAddress[state.language],
    // "addressMainActual must be filled.": words.clearYourBrowserOrAppCache[state.language],
    "success": words.success[state.language],
  };

  dispatch({ type: 'setState', key: 'accessing', value: true });

  const response = await syncHttpRequest2(request, undefined, undefined, 'notification', messages, dispatch);

  dispatch({ type: 'setState', key: 'accessing', value: false });

  if ( response.status === 'fulfilled' ) {
    dispatch({ type: 'setStateMultiLayers', keys: ['creatorInformation', asset, 'editing'], value: false });

    // セッションIDだけが保存されて紛らわしいので、実行しない。もし実行するのであれば、一連のログイン処理も実行する。
    // （ただし、上流で鍵ペアだけは保存しておく。）
    // // session情報更新
    // dispatch({ type: 'setStateMultiLayers', keys: ['session', addressMainActual],
    //   value: {'sessionId': response.body.sessionId.addressMain, 'expirationOfSession': response.body.expirationOfSession.addressMain} });

    // if (addressMainActual !== stateFixed.addressMain) {
    //   dispatch({ type: 'setStateMultiLayers', keys: ['session', addressMainActual],
    //     value: {'sessionId': response.body.sessionId.addressMainActual, 'expirationOfSession': response.body.expirationOfSession.addressMainActual} });
    // }

    // // cookie情報更新(アドレス情報)
    // const userName = state.usersGeneralIndex[stateFixed.addressMain] !== undefined ? state.usersGeneralIndex[stateFixed.addressMain].userName : stateFixed.addressMain;
    // const cookiesNew = [
    //       {
    //         action: 'setUp',
    //         addressType: 'addressMain',
    //         description: `${userName}`,
    //         time: clientTime,
    //         address: stateFixed.addressMain
    //       },
    // ];

    // setAddressesInCookie(state, dispatch, cookies, setCookie, stateFixed.addressMain, cookiesNew);

    // -- 登録カード情報取得
    getRegisteredCard(state, dispatch);
  }
  else {
  }

  return response;
}

export {
  loadDeck,
  handleClickChallengeDuel,
  handleClickGoTop,
  handleClickGoBack,
  handleClickPurchaseItem,
  handleClickDuelHistory,
  handleClickCardList,
  handleClickBuildDeck,
  handleClickDuelRanking,
  handleClickChargePoint,
  handleClickCardRegistration,
  handleClickPlayerStats,
  handleClickSetting,
  buildDuelHistoryStoryText,
  copyDuelHistoryStoryToClipboard,
  downloadDuelHistoryStoryImage,
  copyDuelHistoryKnightBoxesToClipboard,
  downloadDuelHistoryKnightBoxesImage,
  handleClickCheckOut,
  handleClickItemOrderHistory,
  handleClickActivationScreen,
  handleClickClearMnemonic,
  handleClickDisplayMnemonic,
  handleClickDisplayWif,
  addCardToDeck,
  removeCardFromDeck,
  handleClickEditCreatorInformation,
  resetCreatorInformationToRegisteredCard,
};